// Return policy agreement gate (Pipeline).
//
// Blocks every checkout entry point in the cart / cart drawer until the
// customer ticks the "I agree to the return policy" checkbox, and records the
// consent as a cart attribute ("Return policy agreed: Yes") so it shows up on
// the order in the Shopify admin.
//
// Front-end enforcement (the only kind possible without Shopify Plus): it
// closes every door into checkout — the main Checkout button and the
// accelerated buttons (Shop Pay / PayPal / Google Pay). "Buy it now" on
// product pages is funnelled through the cart via a separate theme setting.

const ATTRIBUTE_KEY = 'Return policy agreed';

class ReturnPolicyAgreement extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.return-policy__checkbox');
    this.text = this.querySelector('.return-policy__text');
    this.errorEl = this.querySelector('.return-policy__error');
    // The cart footer wraps both the checkout button and accelerated buttons.
    this.scope = this.closest('[data-cart-bottom]') || this.parentElement;
    if (!this.checkbox || !this.scope) return;

    this.text?.addEventListener('click', this.onTextClick);
    this.checkbox.addEventListener('change', this.onChange);
    // Capture phase so we intercept before the theme's own submit handlers.
    this.scope.addEventListener('click', this.onCtaClick, true);

    this.sync();
  }

  disconnectedCallback() {
    this.text?.removeEventListener('click', this.onTextClick);
    this.checkbox?.removeEventListener('change', this.onChange);
    this.scope?.removeEventListener('click', this.onCtaClick, true);
  }

  get agreed() {
    return Boolean(this.checkbox?.checked);
  }

  // Clicking the label text toggles the box, but let policy links open normally.
  onTextClick = (event) => {
    if (event.target.closest('a')) return;
    this.checkbox.checked = !this.checkbox.checked;
    this.onChange();
  };

  onChange = () => {
    this.sync();
    if (this.agreed) this.hideError();
    this.persist();
  };

  sync = () => {
    const agreed = this.agreed;
    // JS is present, so we take over gating of the main button (which the
    // server rendered `disabled` for no-JS safety) to give click feedback.
    this.scope.querySelectorAll('[name="checkout"]').forEach((btn) => {
      btn.disabled = false;
    });
    this.scope.classList.toggle('return-policy--pending', !agreed);
  };

  onCtaClick = (event) => {
    if (this.agreed) return;
    if (this.contains(event.target)) return; // our own checkbox/label
    const cta = event.target.closest('[name="checkout"], .additional-checkout-buttons');
    if (!cta) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.showError();
  };

  showError = () => {
    if (this.errorEl) this.errorEl.hidden = false;
    this.classList.add('return-policy--error');
    this.checkbox.focus();
  };

  hideError = () => {
    if (this.errorEl) this.errorEl.hidden = true;
    this.classList.remove('return-policy--error');
  };

  // Persist consent to the cart so it rides along to the order, regardless of
  // which checkout path the customer eventually uses.
  persist = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root.replace(/\/$/, '')}/cart/update.js`;
    const attributes = {};
    attributes[ATTRIBUTE_KEY] = this.agreed ? 'Yes' : '';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch(() => {});
  };
}

if (!customElements.get('return-policy-agreement')) {
  customElements.define('return-policy-agreement', ReturnPolicyAgreement);
}
